import React from 'react';
import LiveBadge from './LiveBadge';
import usePlaybackStore from '../../Store/playbackStore';
import '../../Styles/dataStateBadge.css';

const etHHMM = (ms) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(ms));

export default function DataStateBadge({
  status,
  asOf,
  push = false,
  compact = false,
  message,
  onRetry,
}) {
  const replaying = usePlaybackStore((s) => s.mode === 'playing');
  if (replaying) {
    if (compact || push || status === 'live' || status === 'stale' || status === 'closed') {
      return <LiveBadge push={push} />;
    }
    return <div className="dsb-placeholder dsb-placeholder--replay">REPLAY</div>;
  }

  if (status === 'live') return <LiveBadge push={push} />;

  // `closed` is a statement about the exchange; `stale` is a statement about the
  // feed. Saying "market closed" for stale data during trading hours is a lie
  // about the market, so the two read differently.
  if (status === 'closed' || status === 'stale') {
    const time = asOf ? etHHMM(asOf) : null;
    const label = status === 'closed' ? 'market closed' : 'delayed';
    const full = time ? `as of ${time} - ${label}` : label;
    // Compact slots are sized for the LIVE pill -- a chart corner beside the
    // TradingView toolbar, a chain column header. The full sentence is roughly
    // seven times its width and lands on top of whatever is next to it, so
    // compact keeps both facts in the shape those slots were built for.
    if (compact) {
      return (
        <span className={`dsb-stale dsb-stale--compact${push ? ' dsb--push' : ''}`} title={full}>
          {status === 'closed' ? 'CLOSED' : 'DELAYED'}{time ? ` ${time}` : ''}
        </span>
      );
    }
    return (
      <span className={`dsb-stale${push ? ' dsb--push' : ''}`}>
        {full}
      </span>
    );
  }

  if (status === 'error') {
    if (compact) {
      return <span className={`dsb-error-label${push ? ' dsb--push' : ''}`}>ERROR</span>;
    }
    return (
      <div className="dsb-placeholder dsb-placeholder--error" role="alert">
        <strong>Unable to display data</strong>
        <span>{message || 'The data surface could not initialize.'}</span>
        {onRetry && (
          <button type="button" className="dsb-retry" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (compact) return null;

  if (status === 'loading') {
    return (
      <div className="dsb-placeholder dsb-placeholder--loading">
        <span className="dsb-pulse" aria-hidden="true" />
        <span>Loading...</span>
      </div>
    );
  }

  return <div className="dsb-placeholder dsb-placeholder--empty">Market closed - no live data</div>;
}
